/**
 * Cliente API unificado.
 *
 * Arquitectura:
 *  - La auth propia (/api/auth/*) emite un JWT en cookies httpOnly
 *  - Las llamadas same-origin viajan con la cookie automáticamente:
 *    ya no se inyecta ningún header `Authorization`
 *  - Ante un 401, la sesión se renueva vía POST /api/auth/refresh
 *
 * @example
 * ```ts
 * import { apiClient } from '@/lib/api-client'
 *
 * const users = await apiClient.get<User[]>('/users')
 * const newUser = await apiClient.post<User>('/users', { name: 'John' })
 * ```
 */

const API_BASE_URL =
	(process.env.NEXT_PUBLIC_DSAGENT_API_URL ?? process.env.NEXT_PUBLIC_API_URL)?.replace(/\/$/, '') ||
	'http://localhost:4000/api/v1'

// ─── Error Types ────────────────────────────────

export class ApiError extends Error {
	constructor(
		message: string,
		public statusCode: number,
		public details?: unknown,
		public retryAfter?: number
	) {
		super(message)
		this.name = 'ApiError'
	}

	/** Token expirado o inválido → el usuario debe re-autenticarse */
	get isUnauthorized(): boolean {
		return this.statusCode === 401
	}

	/** Sin permisos suficientes */
	get isForbidden(): boolean {
		return this.statusCode === 403
	}
}

// ─── Request Options ────────────────────────────

interface RequestOptions extends Omit<RequestInit, 'body'> {
	/**
	 * Conservado por compatibilidad: la auth ahora viaja en cookie httpOnly,
	 * por lo que no hay header que omitir. No tiene efecto.
	 */
	skipAuth?: boolean

	/**
	 * Custom base URL override.
	 */
	baseUrl?: string

	/**
	 * Request timeout in milliseconds. Defaults to 30000 (30s).
	 */
	timeout?: number
}

// ─── Sesión: renueva las cookies de sesión ante un 401 ─

async function tryRefreshSession(): Promise<boolean> {
	try {
		const res = await fetch('/api/auth/refresh', { method: 'POST' })
		return res.ok
	} catch {
		return false
	}
}

// ─── Core Request Function ──────────────────────

async function request<T>(
	endpoint: string,
	options: RequestOptions & { body?: unknown; _isRetry?: boolean } = {}
): Promise<T> {
	const {
		skipAuth,
		baseUrl = API_BASE_URL,
		timeout = 30_000,
		body,
		_isRetry = false,
		...fetchOptions
	} = options
	void skipAuth

	const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
	const url = `${baseUrl}${cleanEndpoint}`

	// Build headers — la sesión viaja en la cookie httpOnly (same-origin)
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		...(process.env.NEXT_PUBLIC_TENANT_ID && {
			'X-Tenant-Id': process.env.NEXT_PUBLIC_TENANT_ID,
		}),
		...(fetchOptions.headers as Record<string, string>),
	}

	// Create abort controller for timeout
	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), timeout)

	try {
		const response = await fetch(url, {
			...fetchOptions,
			headers,
			body: body ? JSON.stringify(body) : undefined,
			signal: controller.signal,
		})

		// 401 → renovar sesión vía /api/auth/refresh y reintentar una vez
		if (response.status === 401 && !_isRetry) {
			const refreshed = await tryRefreshSession()
			if (refreshed) {
				return request<T>(endpoint, { ...options, _isRetry: true })
			}
		}

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new ApiError(
				errorData.message || `HTTP ${response.status}: ${response.statusText}`,
				response.status,
				errorData,
				errorData.retryAfter
			)
		}

		if (response.status === 204) {
			return {} as T
		}

		return await response.json()
	} catch (error) {
		if (error instanceof ApiError) {
			throw error
		}

		if (error instanceof DOMException && error.name === 'AbortError') {
			throw new ApiError('Request timeout', 408)
		}

		throw new ApiError(error instanceof Error ? error.message : 'Network error', 0)
	} finally {
		clearTimeout(timeoutId)
	}
}

// ─── Public API ─────────────────────────────────

export const apiClient = {
	get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
		return request<T>(endpoint, { ...options, method: 'GET' })
	},

	post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
		return request<T>(endpoint, { ...options, method: 'POST', body })
	},

	put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
		return request<T>(endpoint, { ...options, method: 'PUT', body })
	},

	patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
		return request<T>(endpoint, { ...options, method: 'PATCH', body })
	},

	delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
		return request<T>(endpoint, { ...options, method: 'DELETE' })
	},
} as const

/**
 * SWR fetcher that uses the unified API client.
 * Use this as the default fetcher for SWR hooks.
 *
 * @example
 * ```ts
 * import useSWR from 'swr'
 * import { swrFetcher } from '@/lib/api-client'
 *
 * const { data } = useSWR('/users', swrFetcher)
 * ```
 */
export async function swrFetcher<T>(endpoint: string): Promise<T> {
	return apiClient.get<T>(endpoint)
}
